import * as React from "react";
import { groupBy, partition } from "lodash";
import classnames from "classnames";

import {
  AppShape,
  ConditionShape,
  HurdleShape,
  MessageShape,
  ConditionStatus,
  ConditionStage,
  DocShape,
} from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import ConditionList from "../../shared/Conditions/ConditionList";
import ConditionsPDFModalForm from "./PDFModalForm";
import {
  getConditionsForApplication,
  createConditionsPDF,
  saveConditionsPDF,
} from "../../shared/Conditions/APIHelpers";
import { Button, InlineLoading } from "carbon-components-react";
import { Launch16, Download16, Save16 } from "@carbon/icons-react";
import { eaveLegalName } from "@eave/shared/config/contacts";
import { loanStatusToStr } from "web_modules/shared/loan_statuses";
import { getBorrowersFormattedNames } from "@eave/shared/utils/borrowers";
import { appToPropertyAddress } from "underwriter_dashboard/shared";

const styles = require("./styles.module.scss");
const conditionsPDFFilename = "conditions.pdf";

interface HurdleEditorProps {
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

// A string[][] that destructures to [status, label]. Pithier than a map.
export const conditionOptions = {
  tableOrdering: [
    [ConditionStatus.Open, "Open"],
    [ConditionStatus.Requested, "Requested"],
    [ConditionStatus.Received, "Received"],
    [ConditionStatus.ReadyToReview, "Ready To Review"],
    [ConditionStatus.Cleared, "Cleared"],
    [ConditionStatus.NotApplicable, "Not Applicable"],
  ],
  dropdownOrdering: [
    [ConditionStatus.Open, "Open"],
    [ConditionStatus.Requested, "Requested"],
    [ConditionStatus.Received, "Received"],
    [ConditionStatus.ReadyToReview, "Ready To Review"],
    [ConditionStatus.NotApplicable, "Not Applicable"],
    [ConditionStatus.Cleared, "Cleared"],
  ],
};

class ConditionsHurdleEditor extends React.Component<
  HurdleEditorProps,
  {
    allConditions: ConditionShape[];
    priorToClosingConditions: string[];
    conditionPDFModalFormIsOpen: boolean;
    loading: boolean;
    conditionsPDFDoc: DocShape;
    conditionsPDFS3Key: string;
  }
> {
  state = {
    allConditions: [],
    priorToClosingConditions: [],
    conditionPDFModalFormIsOpen: false,
    loading: false,
    conditionsPDFDoc: null,
    conditionsPDFS3Key: "",
  };

  componentDidMount() {
    this.getConditions();
  }

  updateConditionPDFModalIsOpen(flag: boolean) {
    this.setState(prevState => {
      return { ...prevState, conditionPDFModalFormIsOpen: flag };
    });
  }

  getPriorToClosingConditions(allConditions: ConditionShape[]) {
    return allConditions
      .filter(
        (c: ConditionShape) =>
          c.stage !== ConditionStage.PostClosing && c.status !== ConditionStatus.NotApplicable,
      )
      .map((c: ConditionShape) => c.condition);
  }

  getConditions = () => {
    const {
      app: { id },
    } = this.props;
    getConditionsForApplication(id).then(({ conditions }) => {
      this.setState({
        allConditions: conditions || [],
        priorToClosingConditions: this.getPriorToClosingConditions(conditions) || [],
      });
    });
  };

  appendConditionComments = (id: number, comment: MessageShape, internal: boolean) => {
    this.setState(prevState => {
      let field = "message_thread_id";
      if (internal) {
        field = "internal_message_thread_id";
      }
      return {
        ...prevState,
        allConditions: prevState.allConditions.map(condition => {
          if (condition.id === id) {
            return {
              ...condition,
              [field]: comment.thread_id,
              messages: [...(condition.messages || []), { ...comment, read: true }],
            };
          }
          return condition;
        }),
      };
    });
  };

  markCommentRead = (conditionID: number, messageID: number) => {
    this.setState(prevState => {
      return {
        ...prevState,
        allConditions: prevState.allConditions.map(condition => {
          if (condition.id === conditionID) {
            return {
              ...condition,
              messages: condition.messages.map(message => {
                if (message.id === messageID) {
                  return {
                    ...message,
                    read: true,
                  };
                }
                return message;
              }),
            };
          }
          return condition;
        }),
      };
    });
  };

  getConditionsPDFInitialValues() {
    const { app } = this.props;
    const { priorToClosingConditions } = this.state;

    return {
      loan_number: app.computed_properties?.loan_number,
      property_address: appToPropertyAddress(app) || "",
      borrowers_formatted_names: getBorrowersFormattedNames(app),
      seller_id: "", // we don't need to populate this
      lender_name: eaveLegalName.formal,
      product_name: app.selected_loan_program || "",
      status: loanStatusToStr(app.underwriting_summary?.loan_status) || "",
      status_date: null, // TODO
      gross_loan_amount: Number(app.computed_properties?.loan_amount_total) || 0,
      purpose: app.loan_purpose,
      occupancy: app.property_usage,
      document_type: "", // TODO
      term_months: "", // TODO
      prior_to_closing_conditions_list: priorToClosingConditions || [],
      expiration: null, // TODO
      lock_expiration: null, // TODO
      appraisal_expiration: null, // TODO
      asset_expiration: null, // TODO
      credit_expiration: null, // TODO
      flood_expiration: null, // TODO
      income_expiration: null, // TODO
      mortgage_insurance_expiration: null, // TODO
      title_expiration: null, // TODO
      va_funding_fee_percentage: Number(app.computed_properties?.va_funding_fee_percent) || "",
      va_funding_fee_amount: Number(app.computed_properties?.va_funding_fee_dollars) || "",
      underwriter_name: app.contacts?.loan_underwriter?.name || "",
      underwriter_email: app.contacts?.loan_underwriter?.email || "",
      underwriter_phone: app.contacts?.loan_underwriter?.phone || "",
    };
  }

  preparePayload(values: Record<string, any>) {
    return {
      ...values,
      term_months: values.term_months || null,
      va_funding_fee_percentage: values.va_funding_fee_percentage || null,
      va_funding_fee_amount: values.va_funding_fee_amount || null,
    };
  }

  createConditionsPDF(values: Record<string, any> = null) {
    const { id } = this.props.app;
    const { loading } = this.state;

    if (loading) {
      return;
    }

    this.setState({ conditionsPDFDoc: null, conditionsPDFS3Key: "", loading: true });
    createConditionsPDF(id, this.preparePayload(values))
      .then((resp: any) => {
        this.setState({
          conditionsPDFS3Key: resp.s3_key,
        });
      })
      .catch(e => {
        window.alert("There was a problem generating the document. Please contact eng.");
        throw e;
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  saveConditionsPDF() {
    const { app } = this.props;
    const { conditionsPDFS3Key, loading } = this.state;

    if (loading) {
      return;
    }

    this.setState({ loading: true });
    saveConditionsPDF(app.id, conditionsPDFS3Key)
      .then((resp: any) => {
        this.setState({ conditionsPDFDoc: resp.document });
      })
      .catch(e => {
        window.alert("There was a problem saving the document. Please contact eng.");
        throw e;
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  renderConditionsPDFOptions() {
    const { conditionsPDFDoc, conditionsPDFS3Key, loading } = this.state;

    if (loading) {
      return (
        <InlineLoading status="active" iconDescription="Loading" description="Please wait..." />
      );
    }

    if (conditionsPDFS3Key) {
      const conditionsPDFURL = `/files/${conditionsPDFS3Key}`;
      return (
        <div className={styles.pdfOptionsContainer}>
          <Button
            className={styles.pdfOptionButton}
            target="_blank"
            rel="noreferrer"
            href={conditionsPDFURL}
            kind="tertiary"
            size="small"
            renderIcon={Launch16}
          >
            View PDF
          </Button>
          <Button
            className={styles.pdfOptionButton}
            kind="tertiary"
            size="small"
            download={conditionsPDFFilename}
            href={conditionsPDFURL}
            renderIcon={Download16}
          >
            Download PDF
          </Button>
          {conditionsPDFDoc ? (
            <Button
              className={styles.pdfOptionButton}
              kind="tertiary"
              size="small"
              href={`/underwriter/app/${conditionsPDFDoc.loan_application_id}/DocumentsHurdle?d=${conditionsPDFDoc.id}`}
              renderIcon={Launch16}
            >
              View in documents hurdle
            </Button>
          ) : (
            <Button
              className={styles.pdfOptionButton}
              kind="tertiary"
              size="small"
              renderIcon={Save16}
              onClick={() => this.saveConditionsPDF()}
            >
              Save to documents hurdle
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.pdfOptionsContainer}>
        Click on 'Export to PDF' to generate a document for these conditions
      </div>
    );
  }

  // If multiple calls to renderList will iterate over the same statuses
  // (eg separately for conditions and document requests),
  // a keyPrefix must be passed in (for correct rendering),
  // and a labelPrefix should be as well, for display.
  renderList(list, keyPrefix?: string, labelPrefix?: string) {
    const { app } = this.props;

    return (
      list &&
      conditionOptions.tableOrdering.map(
        ([status, label]) =>
          list[status] && (
            <React.Fragment key={`${keyPrefix ? keyPrefix : ""}${status}-wrapper`}>
              <h4 className={styles.stageHeader}>
                {labelPrefix ? labelPrefix : ""} {label} ({list[status].length})
              </h4>
              <ConditionList
                conditionStatus={status as ConditionStatus}
                displayStage
                editable
                linkedDocuments
                key={`${keyPrefix ? keyPrefix : ""}${status}-conditionList`}
                conditions={list[status]}
                app_id={app.id}
                onUpdate={this.getConditions}
                appendConditionComments={this.appendConditionComments}
                markCommentRead={this.markCommentRead}
              />
            </React.Fragment>
          ),
      )
    );
  }

  render() {
    const { allConditions, conditionPDFModalFormIsOpen } = this.state;
    const { app, hurdle, onHide } = this.props;

    const outstandingStatuses: ConditionStatus[] = [
      ConditionStatus.Open,
      ConditionStatus.Requested,
      ConditionStatus.Received,
    ];
    const [outstanding, notOutstanding] = partition(allConditions, (c: ConditionShape) =>
      outstandingStatuses.includes(c.status),
    );

    const [outstandingDocumentRequests, outstandingConditions] = partition(
      outstanding,
      (c: ConditionShape) => c.is_document_upload_request,
    );

    const groupedOutstandingDocumentRequests = groupBy(
      outstandingDocumentRequests,
      (condition: ConditionShape) => condition.status,
    );
    const groupedOutstandingConditions = groupBy(
      outstandingConditions,
      (condition: ConditionShape) => condition.status,
    );
    const groupedNotOutstanding = groupBy(
      notOutstanding,
      (condition: ConditionShape) => condition.status,
    );

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="dashboard-container">
          <div className="row">
            <div className={classnames("col-md-6 pull-right", styles.header)}>
              <Button
                onClick={() => {
                  this.updateConditionPDFModalIsOpen(true);
                }}
              >
                Export to PDF
              </Button>
            </div>
          </div>
          <div>
            <h4 className={styles.stageHeader}>Conditions PDF</h4>
            {this.renderConditionsPDFOptions()}
          </div>
          <div>
            {this.renderList(
              groupedOutstandingDocumentRequests,
              "doc-requests-",
              "Document Requests -",
            )}
            {this.renderList(groupedOutstandingConditions, "conditions-", "Conditions -")}
            {this.renderList(groupedNotOutstanding)}
          </div>
        </div>
        <ConditionsPDFModalForm
          openModal={conditionPDFModalFormIsOpen}
          initialValues={this.getConditionsPDFInitialValues()}
          onSubmit={(values: Record<string, any>) => {
            this.createConditionsPDF(values);
            this.updateConditionPDFModalIsOpen(false);
          }}
          onRequestClose={() => {
            this.updateConditionPDFModalIsOpen(false);
          }}
        />
      </HurdleView>
    );
  }
}

export default ConditionsHurdleEditor;
